import React from "react";
import { authApi } from "@/api/cities";

interface User {
  id: number;
  email: string;
  full_name: string;
  full_name_fa: string;
  role: string;
  permissions: Record<string, boolean>;
}

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchMe: () => Promise<void>;
}

const AuthContext = React.createContext<AuthStore | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<Omit<AuthStore, "login" | "logout" | "fetchMe">>({
    user: null,
    isAuthenticated: !!localStorage.getItem("access_token"),
    isLoading: false,
  });

  const login = async (email: string, password: string) => {
    setState((s) => ({ ...s, isLoading: true }));
    try {
      const { data } = await authApi.login(email, password);
      localStorage.setItem("access_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      setState((s) => ({ ...s, user: data.user, isAuthenticated: true, isLoading: false }));
    } catch (err) {
      setState((s) => ({ ...s, isLoading: false }));
      throw err;
    }
  };

  const logout = async () => {
    const refresh = localStorage.getItem("refresh_token");
    if (refresh) {
      try { await authApi.logout(refresh); } catch { /* ignore */ }
    }
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    setState((s) => ({ ...s, user: null, isAuthenticated: false }));
  };

  const fetchMe = async () => {
    if (!localStorage.getItem("access_token")) return;
    setState((s) => ({ ...s, isLoading: true }));
    try {
      const { data } = await authApi.me();
      setState((s) => ({ ...s, user: data, isAuthenticated: true, isLoading: false }));
    } catch {
      setState((s) => ({ ...s, user: null, isAuthenticated: false, isLoading: false }));
    }
  };

  React.useEffect(() => {
    fetchMe();
  }, []);

  const value: AuthStore = { ...state, login, logout, fetchMe };

  return React.createElement(AuthContext.Provider, { value }, children);
}

export function useAuth(): AuthStore {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
